/**
 * IBAN validation (ISO 13616, mod-97-10 per ISO 7064).
 *
 * This is deterministic and free, and it runs before a human ever sees the
 * number. An IBAN that fails this check is *definitely* misread — there is no
 * ambiguity to weigh — so it goes straight to the red lane rather than being
 * offered for confirmation.
 */

export type IbanCheck =
  | { valid: true; canonical: string; country: string; formatted: string }
  | { valid: false; reason: IbanFailure }

export type IbanFailure =
  | 'empty'
  | 'too_short'
  | 'too_long'
  | 'bad_characters'
  | 'unknown_country'
  | 'wrong_length_for_country'
  | 'checksum_failed'

/** Length per country. Only the ones plausible for a Belgian family business. */
const COUNTRY_LENGTHS: Record<string, number> = {
  BE: 16, NL: 18, LU: 20, FR: 27, DE: 22, ES: 24, IT: 27,
  PT: 25, AT: 20, CH: 21, GB: 22, IE: 22, DK: 18, SE: 24,
  NO: 15, FI: 18, PL: 28, CZ: 24, HU: 28, RO: 24, BG: 22,
  GR: 27, HR: 21, SI: 19, SK: 24, LT: 20, LV: 21, EE: 20,
  MT: 31, CY: 28,
}

export function normaliseIban(input: string): string {
  return input.replace(/[\s.\-–—]/g, '').toUpperCase()
}

export function validateIban(input: string | null | undefined): IbanCheck {
  if (!input || !input.trim()) return { valid: false, reason: 'empty' }

  const canonical = normaliseIban(input)

  if (canonical.length < 15) return { valid: false, reason: 'too_short' }
  if (canonical.length > 34) return { valid: false, reason: 'too_long' }
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(canonical)) {
    return { valid: false, reason: 'bad_characters' }
  }

  const country = canonical.slice(0, 2)
  const expected = COUNTRY_LENGTHS[country]
  if (expected === undefined) return { valid: false, reason: 'unknown_country' }
  if (canonical.length !== expected) {
    return { valid: false, reason: 'wrong_length_for_country' }
  }

  if (mod97(rearrange(canonical)) !== 1) {
    return { valid: false, reason: 'checksum_failed' }
  }

  return { valid: true, canonical, country, formatted: formatIban(canonical) }
}

/** Groups of four, the way it is printed on an invoice. */
export function formatIban(canonical: string): string {
  return normaliseIban(canonical).replace(/(.{4})/g, '$1 ').trim()
}

/** Move the first four characters to the end, then letters become numbers. */
function rearrange(iban: string): string {
  const moved = iban.slice(4) + iban.slice(0, 4)
  let out = ''
  for (const ch of moved) {
    const code = ch.charCodeAt(0)
    // A–Z → 10–35
    out += code >= 65 && code <= 90 ? String(code - 55) : ch
  }
  return out
}

/**
 * Modulo 97 over a numeric string too long for Number.
 * Chunked so it never exceeds safe integer range.
 */
function mod97(numeric: string): number {
  let remainder = 0
  for (let i = 0; i < numeric.length; i += 7) {
    remainder = Number(String(remainder) + numeric.slice(i, i + 7)) % 97
  }
  return remainder
}
